import { AlertTriangle, FileCheck, Upload, X } from 'lucide-react'
import { useCallback, useRef, useState } from 'react'
import { parseCsvToWorkouts, validateCsvFormat } from '../../lib/csv-parser'
import { UnifiedButton } from './unified-button'

// Type for performance.memory API
interface PerformanceMemory {
  usedJSHeapSize: number
  totalJSHeapSize: number
  jsHeapSizeLimit: number
}

// Extended Performance interface
interface ExtendedPerformance extends Performance {
  memory?: PerformanceMemory
}

interface CsvUploadProps {
  onUpload: (file: File) => Promise<void>
  currentFileName?: string
  onClearCsv?: () => void
  disabled?: boolean
  label?: string
}

export const CsvUpload = ({
  onUpload,
  currentFileName,
  onClearCsv,
  disabled = false,
  label = 'Drop your Hevy CSV or Excel file here, or click to browse'
}: CsvUploadProps) => {
  const [dragActive, setDragActive] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [uploadProgress, setUploadProgress] = useState<string | null>(null)
  const [previewData, setPreviewData] = useState<{
    workoutCount: number
    dateRange: { earliest: string; latest: string }
    exercises: number
  } | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFile = useCallback(
    async (file: File) => {
      setValidationError(null)
      setUploadProgress('Reading file...')

      try {
        // Validate file type
        const fileName = file.name.toLowerCase()
        const validExtensions = ['.csv', '.xls', '.xlsx', '.xlsm', '.xlsb']
        const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext))
        
        if (!hasValidExtension) {
          throw new Error(
            `Invalid file type: "${file.name}" does not appear to be a CSV or Excel file. Please ensure you're uploading a .csv, .xls, .xlsx, .xlsm, or .xlsb file from Hevy.`
          )
        }

        // Validate file size (max 10MB)
        const fileSizeMB = file.size / (1024 * 1024)
        if (file.size > 10 * 1024 * 1024) {
          throw new Error(
            `File too large: ${fileSizeMB.toFixed(2)} MB exceeds the 10 MB limit. Try exporting a smaller date range from Hevy.`
          )
        }

        // Validate file is not empty
        if (file.size === 0) {
          throw new Error(
            'File is empty: The selected file has no content. Please check your Hevy export and try again.'
          )
        }

        // Warn about large files
        if (file.size > 5 * 1024 * 1024) {
          setUploadProgress(`Processing large file (${fileSizeMB.toFixed(2)} MB)...`)
        }

        // Check if file is Excel
        const isExcel = validExtensions.slice(1).some(ext => fileName.endsWith(ext))
        
        // Read file content
        const fileContent = await new Promise<string | ArrayBuffer>((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = (e) => {
            const result = e.target?.result
            if (!result) {
              reject(
                new Error(
                  'FileReader returned empty result. This may be a browser compatibility issue.'
                )
              )
              return
            }
            resolve(result)
          }
          reader.onerror = (error) => {
            console.error('FileReader error:', error)
            reject(
              new Error(
                `Failed to read file: ${reader.error?.message || 'Unknown FileReader error'}. This may be due to browser limitations or file corruption.`
              )
            )
          }
          reader.onabort = () => {
            reject(
              new Error(
                'File reading was aborted. This may happen if the file is too large or the browser ran out of memory.'
              )
            )
          }

          // Add progress tracking for large files
          if (file.size > 5 * 1024 * 1024) {
            // 5MB+
            reader.onprogress = (e) => {
              if (e.lengthComputable) {
                const percentComplete = (e.loaded / e.total) * 100
                setUploadProgress(`Reading file... ${percentComplete.toFixed(0)}%`)
              }
            }
          }

          // Read as ArrayBuffer for Excel files, text for CSV
          if (isExcel) {
            reader.readAsArrayBuffer(file)
          } else {
            reader.readAsText(file, 'UTF-8') // Explicitly specify UTF-8 encoding
          }
        })

        setUploadProgress('Validating format...')

        // Validate file format
        const validation = validateCsvFormat(fileContent, file.name)
        if (!validation.isValid) {
          const fileType = isExcel ? 'Excel' : 'CSV'
          const contentPreview = typeof fileContent === 'string' 
            ? `"${fileContent.substring(0, 200)}..."` 
            : `ArrayBuffer (${(fileContent as ArrayBuffer).byteLength} bytes)`
          const detailedError =
            `${fileType} format validation failed: ${validation.error || 'Unknown format error'}\n\n` +
            `File details:\n` +
            `- Size: ${fileSizeMB.toFixed(2)} MB\n` +
            `- Content preview: ${contentPreview}\n\n` +
            `Make sure you're using a direct ${fileType} export from Hevy.`
          throw new Error(detailedError)
        }

        setUploadProgress('Parsing workouts...')

        // Parse and validate workouts - add more detailed error handling
        let workoutData
        try {
          workoutData = parseCsvToWorkouts(fileContent, file.name)
        } catch (parseError) {
          // Enhanced error message for parsing failures
          const errorMessage =
            parseError instanceof Error ? parseError.message : 'Unknown parsing error'
          console.error('CSV parsing failed:', parseError)

          // Provide helpful guidance based on common issues
          if (errorMessage.includes('Missing required columns')) {
            throw new Error(
              `Column validation failed: ${errorMessage}\n\n` +
                `Expected columns: title, start_time, exercise_title\n` +
                `Found columns: ${typeof fileContent === 'string' ? Object.keys(JSON.parse(fileContent.split('\n')[0] || '{}')) : 'Unable to preview Excel columns'}\n\n` +
                `Make sure your file is a direct export from Hevy.`
            )
          } else if (errorMessage.includes('No valid workout data')) {
            throw new Error(
              `No workout data found: ${errorMessage}\n\n` +
                `File contains ${typeof fileContent === 'string' ? fileContent.split('\n').length : 'N/A (Excel file)'} lines but no valid workout records.\n` +
                `Ensure your CSV contains workout records with exercises, weights, and reps.`
            )
          } else {
            throw new Error(
              `File parsing error: ${errorMessage}\n\n` +
                `File size: ${fileSizeMB.toFixed(2)} MB\n` +
                `Line count: ${typeof fileContent === 'string' ? fileContent.split('\n').length : 'N/A (Excel file)'}\n` +
                `First line: ${typeof fileContent === 'string' ? `"${fileContent.split('\n')[0]}"` : 'N/A (Excel file)'}\n\n` +
                `Please verify your file is a valid Hevy export.`
            )
          }
        }

        if (workoutData.workouts.length === 0) {
          throw new Error(
            `No valid workouts found in CSV file.\n\n` +
              `File analysis:\n` +
              `- File size: ${fileSizeMB.toFixed(2)} MB\n` +
              `- Lines processed: ${typeof fileContent === 'string' ? fileContent.split('\n').length : 'N/A (Excel file)'}\n` +
              `- Workouts found: 0\n\n` +
              `Please ensure your CSV contains workout data with exercises and sets. ` +
              `Try exporting from Hevy again or contact support if the issue persists.`
          )
        }

        // Set preview data
        setPreviewData({
          workoutCount: workoutData.workouts.length,
          dateRange: {
            earliest: workoutData.workouts[workoutData.workouts.length - 1]?.start_time,
            latest: workoutData.workouts[0]?.start_time
          },
          exercises: Array.from(
            new Set(workoutData.workouts.flatMap((w) => w.exercises.map((e) => e.title)))
          ).length
        })

        setUploadProgress('Uploading to storage...')

        // Upload the file
        await onUpload(file)

        setUploadProgress('Upload complete!')
        setTimeout(() => setUploadProgress(null), 2000)
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Upload failed'
        setValidationError(errorMessage)
        setUploadProgress(null)
        setPreviewData(null)

        // Log detailed error information for debugging
        const errorContext = {
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
          lastModified: new Date(file.lastModified).toISOString(),
          browser: navigator.userAgent,
          timestamp: new Date().toISOString(),
          memoryUsage: (() => {
            const extPerf = performance as ExtendedPerformance
            return extPerf.memory
              ? {
                  usedJSHeapSize: extPerf.memory.usedJSHeapSize,
                  totalJSHeapSize: extPerf.memory.totalJSHeapSize,
                  jsHeapSizeLimit: extPerf.memory.jsHeapSizeLimit
                }
              : 'unavailable'
          })(),
          error: error
        }

        console.error('CSV upload error:', errorContext)

        // Create user-friendly error message with debug info
        const debugInfo =
          `\n\n🔍 Debug Info (please include in bug report):\n` +
          `File: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)\n` +
          `Browser: ${navigator.userAgent.includes('Chrome') ? 'Chrome' : navigator.userAgent.includes('Firefox') ? 'Firefox' : navigator.userAgent.includes('Safari') ? 'Safari' : 'Other'}\n` +
          `Error Type: ${error instanceof Error ? error.constructor.name : 'Unknown'}\n` +
          `Timestamp: ${new Date().toLocaleString()}`

        setValidationError(errorMessage + debugInfo)
      }
    },
    [onUpload]
  )

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setDragActive(false)

      if (disabled) return

      const files = Array.from(e.dataTransfer.files)
      if (files.length > 0) {
        handleFile(files[0])
      }
    },
    [handleFile, disabled]
  )

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (disabled) return

      const files = e.target.files
      if (files && files.length > 0) {
        handleFile(files[0])
      }
    },
    [handleFile, disabled]
  )

  const triggerFileInput = useCallback(() => {
    if (disabled) return
    fileInputRef.current?.click()
  }, [disabled])

  // If we have a current file, show the uploaded state
  if (currentFileName) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-900/20">
          <div className="flex items-center gap-3">
            <FileCheck className="h-5 w-5 text-green-600 dark:text-green-400" />
            <div>
              <p className="text-sm font-medium text-green-800 dark:text-green-200">
                CSV Uploaded: {currentFileName}
              </p>
              {previewData && (
                <p className="text-xs text-green-600 dark:text-green-400">
                  {previewData.workoutCount} workouts • {previewData.exercises} exercises
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {onClearCsv && (
              <UnifiedButton
                variant="outline"
                size="sm"
                onClick={onClearCsv}
                className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
              >
                <X className="h-4 w-4" />
              </UnifiedButton>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div
        className={`relative rounded-lg border-2 border-dashed p-6 text-center transition-colors ${
          disabled
            ? 'border-muted-foreground/20 bg-muted/20 cursor-not-allowed'
            : dragActive
              ? 'border-primary bg-primary/10'
              : 'border-muted-foreground/30 hover:border-muted-foreground/50 cursor-pointer'
        } `}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={triggerFileInput}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xls,.xlsx,.xlsm,.xlsb"
          onChange={handleFileInput}
          className="hidden"
          disabled={disabled}
        />

        <div className="space-y-2">
          <Upload
            className={`mx-auto h-8 w-8 ${
              disabled ? 'text-muted-foreground/30' : 'text-muted-foreground'
            }`}
          />
          <div>
            <p
              className={`text-sm font-medium ${
                disabled ? 'text-muted-foreground/50' : 'text-foreground'
              }`}
            >
              {disabled ? 'CSV upload disabled' : label}
            </p>
            <p className="text-muted-foreground mt-1 text-xs">Supports .csv and Excel files up to 10MB</p>
          </div>
        </div>

        {uploadProgress && (
          <div className="bg-background/90 absolute inset-0 flex items-center justify-center rounded-lg">
            <div className="text-center">
              <div className="border-primary mx-auto mb-2 h-6 w-6 animate-spin rounded-full border-b-2"></div>
              <p className="text-sm font-medium">{uploadProgress}</p>
            </div>
          </div>
        )}
      </div>

      {validationError && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/20">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-600 dark:text-red-400" />
          <div className="text-sm whitespace-pre-line text-red-800 dark:text-red-200">
            {validationError}
          </div>
        </div>
      )}
    </div>
  )
}
